<template>
  <v-card color="transparent" elevation="0" class="dashboard-wrapper">
    <v-col v-if="!finishedLoading || isFiltering" class="d-flex justify-center flex-grow mt-16">
      <v-progress-circular indeterminate color="primary" size="64" width="6"></v-progress-circular>
    </v-col>

    <div v-if="finishedLoading && centerData && !isFiltering" class="dashboard-container">
      <v-row class="mb-6 px-4 align-end">
        <div>
          <h1 class="text-h4 white--text font-weight-bold mb-1">Vertrags-Analytics</h1>
          <p class="grey--text text--lighten-1 mb-0">Performance Monitoring & Benchmark</p>
        </div>
        <v-spacer></v-spacer>

        <div class="glass-control pa-1 d-flex mr-4">
          <v-btn
            v-for="range in [12, 24, 36]"
            :key="range"
            x-small
            depressed
            :color="selectedRange === range ? 'success' : 'transparent'"
            class="rounded-lg transition-swing mx-1 btn-text-white"
            @click="selectedRange = range">
            {{ range / 12 }} J
          </v-btn>
        </div>

        <div class="glass-control pa-1 d-flex mr-4">
          <v-btn
            x-small
            depressed
            :color="viewMode === 'total' ? 'success' : 'transparent'"
            class="rounded-lg mx-1 btn-text-white"
            @click="viewMode = 'total'">
            Bestand
          </v-btn>
          <v-btn
            x-small
            depressed
            :color="viewMode === 'diff' ? 'success' : 'transparent'"
            class="rounded-lg mx-1 btn-text-white"
            @click="viewMode = 'diff'">
            Fluktuation
          </v-btn>
        </div>

        <div class="glass-control pa-1 d-flex">
          <v-btn
            x-small
            depressed
            :color="displayMode === 'chart' ? 'success' : 'transparent'"
            class="rounded-lg mx-1 btn-text-white"
            @click="displayMode = 'chart'">
            <v-icon left small>mdi-chart-bar</v-icon> Chart
          </v-btn>
          <v-btn
            x-small
            depressed
            :color="displayMode === 'table' ? 'success' : 'transparent'"
            class="rounded-lg mx-1 btn-text-white"
            @click="displayMode = 'table'">
            <v-icon left small>mdi-table</v-icon> Tabelle
          </v-btn>
        </div>
      </v-row>

      <v-row dense class="mb-6" v-if="viewMode === 'total'">
        <v-col cols="12" md="3" v-for="(stat, i) in mainStats" :key="i">
          <v-card class="stats-card pa-4" dark>
            <div class="text-overline grey--text mb-1">{{ stat.label }}</div>
            <div class="d-flex align-center flex-wrap">
              <span class="text-h5 font-weight-bold mr-2">{{ stat.value }}</span>
              <v-chip v-if="stat.trend !== undefined" x-small :color="stat.trend >= 0 ? 'success' : 'error'" class="font-weight-bold">
                {{ stat.trend >= 0 ? "+" : "" }}{{ Number(stat.trend).toFixed(1) }}%
              </v-chip>
            </div>
            <div class="text-caption grey--text mt-1">{{ stat.subtext }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mb-6" v-else-if="viewMode === 'diff'">
        <v-col cols="12">
          <v-card class="stats-card pa-4" dark>
            <div class="d-flex align-center justify-space-between mb-4 flex-wrap">
              <div>
                <div class="text-overline grey--text mb-1">Fluktuations-Ranking</div>
                <div class="text-caption grey--text">Vergleiche die durchschnittliche Fluktuation der Standorte.</div>
              </div>
              <div class="d-flex align-center mt-2 mt-sm-0">
                <v-select
                  v-model="rankingTimeframe"
                  :items="rankingOptions"
                  dense
                  outlined
                  hide-details
                  class="mr-4 custom-select-small"
                  style="max-width: 180px"
                  color="success"
                  item-color="success"></v-select>
                <v-btn icon outlined color="grey lighten-1" @click="rankingSortDesc = !rankingSortDesc">
                  <v-icon>{{ rankingSortDesc ? "mdi-sort-descending" : "mdi-sort-ascending" }}</v-icon>
                </v-btn>
              </div>
            </div>

            <v-slide-group show-arrows dark>
              <v-slide-item v-for="(studio, i) in fluctuationRanking" :key="studio.code">
                <v-card
                  class="mr-4 pa-3 glass-card text-center d-flex flex-column justify-center align-center"
                  width="130"
                  height="110"
                  :color="i === 0 ? (rankingSortDesc ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)') : 'rgba(255, 255, 255, 0.03)'"
                  style="border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 12px; cursor: default">
                  <div class="text-h5 font-weight-black mb-1" :class="i === 0 ? (rankingSortDesc ? 'error--text' : 'success--text') : 'white--text'">
                    #{{ studio.rank }}
                  </div>
                  <div class="text-subtitle-2 font-weight-medium text-truncate white--text mb-2" style="width: 100%" :title="studio.center">
                    {{ studio.center }}
                  </div>
                  <v-chip x-small :color="studio.average < 15 ? 'success' : studio.average > 25 ? 'error' : 'warning'">
                    Ø {{ studio.displayAvg }}%
                  </v-chip>
                </v-card>
              </v-slide-item>

              <v-slide-item v-if="fluctuationRanking.length === 0">
                <div class="pa-4 grey--text text-caption">Keine Daten für diesen Zeitraum.</div>
              </v-slide-item>
            </v-slide-group>
          </v-card>
        </v-col>
      </v-row>

      <v-card class="main-chart-card mb-8" dark>
        <v-card-title class="d-flex align-center py-4 px-6 flex-wrap">
          <span class="font-weight-light">{{ viewMode === "total" ? "Gesamtentwicklung (Alle Studios)" : "Fluktuation (Alle Studios)" }}</span>
          <v-chip x-small class="ml-4" color="blue-grey darken-3">⌀ {{ centerData.totalStats.average }} Mitglieder</v-chip>

          <div class="d-flex flex-wrap ml-4" v-if="viewMode === 'diff'">
            <v-tooltip top v-for="yf in centerData.totalStats.totalYearlyFluctuations" :key="yf.year">
              <template v-slot:activator="{ on, attrs }">
                <v-chip x-small outlined color="grey lighten-1" class="mr-2" v-bind="attrs" v-on="on">
                  Ø Fluktuation {{ yf.year }}: {{ yf.average }}%
                </v-chip>
              </template>
              <span>{{ yf.months }} {{ yf.year }}</span>
            </v-tooltip>
          </div>

          <v-menu offset-y v-if="displayMode === 'table'">
            <template v-slot:activator="{ on, attrs }">
              <v-btn x-small outlined color="grey lighten-1" class="ml-4" v-bind="attrs" v-on="on">
                <v-icon left small>mdi-download</v-icon> Download CSV
              </v-btn>
            </template>
            <v-list dense dark class="grey darken-3">
              <v-list-item class="pointer" @click="downloadCsv('total')">
                <v-list-item-title>Gesamtzahlen</v-list-item-title>
              </v-list-item>
              <v-list-item class="pointer" @click="downloadCsv('all_studios')">
                <v-list-item-title>Alle Studios (Detailliert)</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-spacer></v-spacer>
          <v-chip x-small outlined color="grey lighten-1">Analyse: Letzte {{ selectedRange }} Monate</v-chip>
        </v-card-title>
        <v-card-text>
          <apexchart
            v-if="displayMode === 'chart'"
            type="bar"
            height="400"
            :options="getChartOptions(true)"
            :series="centerData.mainSeries"></apexchart>
          <v-simple-table v-else dark class="transparent custom-table" height="600" fixed-header>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left font-weight-bold" style="color: #9e9e9e !important">Monat</th>
                  <th class="text-right font-weight-bold" style="color: #9e9e9e !important" v-for="s in centerData.mainSeries" :key="s.name">
                    {{ s.name }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(cat, i) in centerData.categories" :key="cat">
                  <td style="border-bottom: 1px dashed rgba(255, 255, 255, 0.05) !important">{{ cat }}</td>
                  <td
                    class="text-right"
                    style="border-bottom: 1px dashed rgba(255, 255, 255, 0.05) !important"
                    v-for="s in centerData.mainSeries"
                    :key="s.name">
                    {{ s.data[i] !== undefined && s.data[i] !== null ? s.data[i].toLocaleString() : 0 }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <v-row class="mt-8 mb-4 px-2 align-center">
        <v-divider dark class="mr-4"></v-divider>
        <span class="text-overline grey--text text--lighten-1 text-no-wrap">Detailanalyse aller Standorte</span>
        <v-divider dark class="ml-4"></v-divider>
      </v-row>

      <v-row>
        <v-col cols="12" v-for="studio in centerData.returnData" :key="studio.center">
          <v-card class="studio-card elevation-10 d-flex flex-column fill-height" dark>
            <v-card-title class="d-flex justify-space-between py-4 px-6 secondary-bg">
              <div style="max-width: 70%">
                <div class="d-flex align-center">
                  <span class="text-h6 font-weight-bold text-truncate d-block">{{ studio.center }}</span>
                  <v-btn v-if="displayMode === 'table'" x-small icon color="grey lighten-1" class="ml-2" @click="downloadCsv('studio', studio)">
                    <v-icon small>mdi-download</v-icon>
                  </v-btn>
                </div>
                <div class="d-flex flex-wrap mt-1" v-if="viewMode === 'diff'">
                  <v-tooltip top v-for="yf in studio.yearlyFluctuations" :key="yf.year">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip x-small outlined color="grey lighten-1" class="mr-2 mt-1" v-bind="attrs" v-on="on">
                        Ø Fluktuation {{ yf.year }}: {{ yf.average }}%
                      </v-chip>
                    </template>
                    <span>{{ yf.months }} {{ yf.year }}</span>
                  </v-tooltip>
                </div>
              </div>
              <div class="text-right">
                <div class="text-h6">⌀ {{ studio.stats.average }}</div>
                <div class="text-caption grey--text">Ø / Monat</div>
              </div>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text class="px-6 py-6 flex-grow-1">
              <div
                style="min-height: 380px; position: relative"
                class="chart-container-lazy"
                v-intersect.once="{
                  handler: (entries, observer) => onInteract(entries, observer, studio.center),
                  options: {
                    threshold: 0.1,
                  },
                }">
                <v-progress-circular
                  v-if="!loadedStudios.includes(studio.center)"
                  indeterminate
                  color="grey darken-2"
                  size="50"></v-progress-circular>

                <template v-else>
                  <apexchart
                    v-if="displayMode === 'chart'"
                    width="100%"
                    type="bar"
                    height="380"
                    :options="getChartOptions(false, studio)"
                    :series="studio.series"></apexchart>
                  <div v-else style="height: 600px; overflow-y: auto">
                    <v-simple-table dark class="transparent custom-table">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left font-weight-bold" style="color: #9e9e9e !important">Monat</th>
                            <th class="text-right font-weight-bold" style="color: #9e9e9e !important" v-for="s in studio.series" :key="s.name">
                              {{ s.name }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(cat, i) in centerData.categories" :key="cat">
                            <td style="border-bottom: 1px dashed rgba(255, 255, 255, 0.05) !important">{{ cat }}</td>
                            <td
                              class="text-right"
                              style="border-bottom: 1px dashed rgba(255, 255, 255, 0.05) !important"
                              v-for="s in studio.series"
                              :key="s.name">
                              {{ s.data[i] !== undefined && s.data[i] !== null ? s.data[i].toLocaleString() : 0 }}
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>
                </template>
              </div>

              <div class="d-flex justify-space-between mt-6 pt-4 border-top">
                <div class="d-flex flex-column">
                  <span class="text-caption grey--text mb-1">Aktueller IST-Stand</span>
                  <span class="text-h5 white--text">{{ studio.stats.currentMonth.ist }}</span>
                </div>

                <div class="d-flex flex-column text-right">
                  <span class="text-caption grey--text mb-1">Zielerreichung</span>
                  <div>
                    <span class="text-h5 mr-2">{{ studio.stats.currentMonth.plan }}</span>
                    <v-chip small :color="studio.stats.currentMonth.diffPercent >= 0 ? 'success' : 'error'" outlined>
                      {{ studio.stats.currentMonth.diffPercent >= 0 ? "+" : "" }}{{ studio.stats.currentMonth.diffPercent.toFixed(1) }}%
                    </v-chip>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="centerData.returnData.length === 0" justify="center" class="mt-12">
        <v-col cols="12" class="text-center opacity-60">
          <div class="pa-10">
            <h3 class="text-h5 font-weight-light white--text">Keine Daten verfügbar</h3>
          </div>
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import api from "../../../api"
import VueApexCharts from "vue-apexcharts"
import { mapState } from "vuex"
import moment from "moment"

export default {
  name: "HomeDashboardContractDevelopment",
  components: { apexchart: VueApexCharts },
  props: { studios: { type: Array, default: () => [] } },
  data() {
    return {
      finishedLoading: false,
      isFiltering: false,
      rawBackendData: null,
      selectedRange: 12,
      viewMode: "total",
      displayMode: "chart",

      loadedStudios: [],

      rankingTimeframe: "12m",
      rankingSortDesc: false,
    }
  },
  async created() {
    await this.fetchData()
  },
  methods: {
    onInteract(entries, observer, studioName) {
      if (entries[0].isIntersecting) {
        if (!this.loadedStudios.includes(studioName)) {
          requestAnimationFrame(() => {
            this.loadedStudios.push(studioName)
          })
        }
      }
    },
    async fetchData() {
      this.finishedLoading = false
      this.loadedStudios = []

      try {
        const start = moment().subtract(3, "years").startOf("month").format("YYYY-MM-DD")
        const end = moment().endOf("month").format("YYYY-MM-DD")

        const contractRes = await api.fetchContractDevelopment({
          range: [start, end],
          studios: this.studios,
        })
        this.rawBackendData = contractRes.centers

        this.finishedLoading = true
      } catch (e) {
        console.error("API Fehler:", e)
        this.finishedLoading = true
      }
    },
    downloadCsv(type, studio = null) {
      let csvLines = []
      let headers = []
      let rows = []

      if (type === "total") {
        headers = ["Monat", ...this.centerData.mainSeries.map((s) => s.name)]
        rows = this.centerData.categories.map((cat, i) => {
          const rowData = this.centerData.mainSeries.map((s) => (s.data[i] !== undefined && s.data[i] !== null ? s.data[i] : 0))
          return [cat, ...rowData].join(";")
        })
        csvLines.push(headers.join(";"))
        csvLines.push(...rows)
      } else if (type === "studio" && studio) {
        headers = ["Monat", ...studio.series.map((s) => s.name)]
        rows = this.centerData.categories.map((cat, i) => {
          const rowData = studio.series.map((s) => (s.data[i] !== undefined && s.data[i] !== null ? s.data[i] : 0))
          return [cat, ...rowData].join(";")
        })
        csvLines.push(headers.join(";"))
        csvLines.push(...rows)
      } else if (type === "all_studios") {
        const seriesNames = this.centerData.returnData[0].series.map((s) => s.name)
        headers = ["Monat", "Studio", ...seriesNames]
        csvLines.push(headers.join(";"))

        this.centerData.categories.forEach((cat, i) => {
          this.centerData.returnData.forEach((st) => {
            const rowData = st.series.map((s) => (s.data[i] !== undefined && s.data[i] !== null ? s.data[i] : 0))
            csvLines.push([cat, st.center, ...rowData].join(";"))
          })
        })
      }

      const csvContent = csvLines.join("\n")
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" })
      const link = document.createElement("a")
      const url = URL.createObjectURL(blob)
      link.setAttribute("href", url)

      let fileName = "export.csv"
      if (type === "total") fileName = `Gesamtentwicklung_${this.viewMode}.csv`
      if (type === "studio") fileName = `Studio_${studio.center.replace(/\s+/g, "_")}_${this.viewMode}.csv`
      if (type === "all_studios") fileName = `Alle_Studios_Detailliert_${this.viewMode}.csv`

      link.setAttribute("download", fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    calculatePercentageDiff(actual, planned) {
      if (!planned || planned === 0) return actual > 0 ? 100.0 : 0.0
      return ((actual - planned) / planned) * 100
    },
    getChartOptions(isMainChart, studio = null) {
      const categories = this.centerData ? this.centerData.categories : []
      const isDiffView = (isMainChart && this.viewMode === "diff") || (!isMainChart && this.viewMode === "diff")

      const colors = isDiffView ? ["#00E396", "#008FFB", "#FF5252"] : ["#2483af", "#4CAF50"]

      return {
        chart: {
          stacked: isDiffView,
          toolbar: { show: false },
          background: "transparent",
          foreColor: "#9e9e9e",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800,
            dynamicAnimation: {
              enabled: true,
              speed: 350,
            },
          },
        },
        plotOptions: { bar: { horizontal: false, columnWidth: "50%", borderRadius: 4 } },
        dataLabels: { enabled: false },
        xaxis: { categories, labels: { style: { fontSize: "11px" } } },
        yaxis: { labels: { style: { fontSize: "11px" } } },
        grid: { borderColor: "rgba(255,255,255,0.05)", strokeDashArray: 4 },
        colors: colors,
        legend: { position: "top", horizontalAlign: "right" },
        tooltip: {
          theme: "dark",
          custom: ({ series, dataPointIndex, w }) => {
            const monat = w.globals.labels[dataPointIndex]

            if (isDiffView) {
              const renewals = series[0][dataPointIndex] || 0
              const newSales = series[1][dataPointIndex] || 0
              const cancellations = series[2][dataPointIndex] || 0

              let activeMembers = null
              if (isMainChart) {
                activeMembers = this.centerData.totalStats.tacActiveData[dataPointIndex]
              } else if (studio) {
                activeMembers = studio.tacActiveData[dataPointIndex]
              }

              let fluctuationHtml = `<div class="d-flex justify-space-between pt-1 mt-1 border-top-tooltip">
                  <span>Fluktuation (p.a.):</span>
                  <span class="text-caption grey--text">Keine TAC-Daten (vor Sept 24)</span>
                </div>`

              let activeMembersHtml = `<div class="d-flex justify-space-between mb-1">
                  <span>Bestand (Basis):</span><span class="text-caption grey--text">Unbekannt</span>
                </div>`

              if (activeMembers !== null) {
                activeMembersHtml = `<div class="d-flex justify-space-between mb-1">
                  <span>Bestand (Basis):</span><b style="color: #fff">${activeMembers.toLocaleString()}</b>
                </div>`

                let fluctuationPerc = 0
                if (activeMembers > 0) fluctuationPerc = (cancellations * 12 * 100) / activeMembers

                fluctuationHtml = `<div class="d-flex justify-space-between pt-1 mt-1 border-top-tooltip">
                  <span>Fluktuation (p.a.):</span>
                  <b>${fluctuationPerc.toFixed(1)}%</b>
                </div>`
              }

              return `<div class="pa-3 custom-tooltip-modern" style="min-width: 230px;">
                <div class="mb-2"><strong>${monat}</strong></div>
                ${activeMembersHtml}
                <div class="d-flex justify-space-between mb-1"><span>Erneuerungen:</span><b style="color: #00E396">${renewals}</b></div>
                <div class="d-flex justify-space-between mb-1"><span>Neukunden:</span><b style="color: #008FFB">${newSales}</b></div>
                <div class="d-flex justify-space-between mb-1"><span>Kündigungen:</span><b style="color: #FF5252">${cancellations}</b></div>
                ${fluctuationHtml}
              </div>`
            }

            const soll = series[0][dataPointIndex]
            const ist = series[1][dataPointIndex]
            const diff = ist - soll
            let vormonatHtml = ""
            if (dataPointIndex > 0) {
              const prevIst = series[1][dataPointIndex - 1]
              const vDiff = ist - prevIst
              const vPerc = prevIst !== 0 ? (vDiff / prevIst) * 100 : 0
              vormonatHtml = `<div class="mt-2 pt-1 border-top-tooltip d-flex justify-space-between"><span>vs. Vormonat (Ist):</span><b style="color:${
                vDiff >= 0 ? "#10B981" : "#EF4444"
              }">${vDiff >= 0 ? "+" : ""}${vDiff} (${vPerc.toFixed(1)}%)</b></div>`
            }
            return `<div class="pa-3 custom-tooltip-modern" style="min-width: 220px;">
                <div class="mb-2"><strong>${monat}</strong></div>
                <div class="d-flex justify-space-between mb-1"><span>Soll:</span><b>${soll.toLocaleString()}</b></div>
                <div class="d-flex justify-space-between mb-1"><span>Ist:</span><b>${ist.toLocaleString()}</b></div>
                <div class="d-flex justify-space-between pt-1 border-top-tooltip"><span>Differenz:</span><b style="color:${
                  diff >= 0 ? "#10B981" : "#EF4444"
                }">${diff >= 0 ? "+" : ""}${diff.toLocaleString()}</b></div>
                ${vormonatHtml}
              </div>`
          },
        },
      }
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),

    availableRankingYears() {
      if (!this.rawBackendData) return []
      const allLoadedMonths = Object.keys(this.rawBackendData[Object.keys(this.rawBackendData)[0]]).sort()
      const years = new Set(allLoadedMonths.map((m) => m.split("-")[0]))
      return Array.from(years).sort().reverse()
    },

    rankingOptions() {
      const opts = [{ text: "Letzte 12 Monate", value: "12m" }]
      this.availableRankingYears.forEach((y) => {
        opts.push({ text: `Jahr ${y}`, value: y })
      })
      return opts
    },

    fluctuationRanking() {
      if (!this.rawBackendData || !this.storeStudios.length) return []

      const allLoadedMonths = Object.keys(this.rawBackendData[Object.keys(this.rawBackendData)[0]]).sort()
      const currentMonthStr = moment().format("YYYY-MM")
      let targetMonths = []

      if (this.rankingTimeframe === "12m") {
        targetMonths = allLoadedMonths.filter((m) => m <= currentMonthStr).slice(-12)
      } else {
        targetMonths = allLoadedMonths.filter((m) => m.startsWith(this.rankingTimeframe) && m <= currentMonthStr)
      }

      if (targetMonths.length === 0) return []

      const ranking = []

      this.studios.forEach((studio) => {
        const stats = this.rawBackendData[studio.code] || {}
        let sumFluct = 0
        let validMonths = 0

        targetMonths.forEach((m) => {
          const d = stats[m] || { active: 0, cancellations: 0 }
          const cancelCount = d.cancellations || 0

          let activeMembers = null
          if (typeof d === "object" && d.tacActiveMembers !== undefined) {
            activeMembers = d.tacActiveMembers
          } else if (typeof d === "object") {
            // Fallback falls es kein Object war
            activeMembers = d.active || 0
          }

          // Fluktuation nur berechnen, wenn valide TAC Daten vorhanden sind (ab Sept 2024)
          if (activeMembers !== null && activeMembers > 0) {
            let fluct = (cancelCount * 12 * 100) / activeMembers
            sumFluct += fluct
            validMonths++
          }
        })

        const avg = validMonths > 0 ? sumFluct / validMonths : 0
        const displayAvg = avg.toFixed(1)

        if (Number(displayAvg) > 0) {
          ranking.push({
            center: studio.name,
            code: studio.code,
            average: avg,
            displayAvg: displayAvg,
          })
        }
      })

      ranking.sort((a, b) => {
        if (this.rankingSortDesc) return b.average - a.average
        return a.average - b.average
      })

      ranking.forEach((r, idx) => (r.rank = idx + 1))

      return ranking
    },

    centerData() {
      if (!this.rawBackendData || !this.storeStudios.length) return null

      const allLoadedMonths = Object.keys(this.rawBackendData[Object.keys(this.rawBackendData)[0]]).sort()
      const categories = allLoadedMonths.slice(-this.selectedRange)
      const returnData = []

      const monthlyTotals = categories.reduce(
        (acc, m) => ({ ...acc, [m]: { ist: 0, plan: 0, new: 0, renewals: 0, cancellations: 0, tacActive: 0, validTacStudios: 0 } }),
        {},
      )

      const monthsByYear = {}
      categories.forEach((m) => {
        const year = m.split("-")[0]
        if (!monthsByYear[year]) monthsByYear[year] = []
        monthsByYear[year].push(m)
      })
      const availableYears = Object.keys(monthsByYear).sort()

      const currentMonthStr = moment().format("YYYY-MM")

      this.studios.forEach((studio) => {
        const stats = this.rawBackendData[studio.code] || {}
        const studioIst = [],
          studioSoll = [],
          studioCancel = [],
          studioNew = [],
          studioRenew = [],
          studioTacActive = []

        let lastIst = 0

        const yearlyFluctuations = availableYears
          .map((year) => {
            const chunk = monthsByYear[year].filter((m) => m <= currentMonthStr)

            if (chunk.length === 0) return null

            let sumFluct = 0
            let validMonths = 0

            chunk.forEach((m) => {
              const d = stats[m] || { active: 0, cancellations: 0 }
              const cancelCount = d.cancellations || 0
              const activeMembers = typeof d === "object" ? (d.tacActiveMembers !== undefined ? d.tacActiveMembers : d.active) : d

              // Fluktuation nur berechnen, wenn valide Daten (tacActiveMembers !== null) vorhanden sind
              if (activeMembers !== null && activeMembers > 0) {
                let fluct = (cancelCount * 12 * 100) / activeMembers
                sumFluct += fluct
                validMonths++
              }
            })

            const startMonth = moment(chunk[0], "YYYY-MM").format("MMM")
            const endMonth = moment(chunk[chunk.length - 1], "YYYY-MM").format("MMM")

            return {
              year: year,
              months: startMonth === endMonth ? startMonth : `${startMonth} - ${endMonth}`,
              average: validMonths > 0 ? (sumFluct / validMonths).toFixed(1) : "0.0",
            }
          })
          .filter(Boolean)

        categories.forEach((m) => {
          const d = stats[m] || { active: 0, renewals: 0, newSales: 0, cancellations: 0 }
          const ist = typeof d === "object" ? d.active || 0 : d

          const tacActive = typeof d === "object" ? (d.tacActiveMembers !== undefined ? d.tacActiveMembers : null) : null

          let plan = lastIst
          const growth = (this.storeStudios.find((s) => s.code === studio.code)?.growth || []).find((g) => g.date === m)
          if (growth && lastIst > 0) plan += Number(growth.count)

          let cancelCount = d.cancellations || 0

          studioIst.push(ist)
          studioSoll.push(plan)
          studioCancel.push(cancelCount)
          studioNew.push(d.newSales || 0)
          studioRenew.push(d.renewals || 0)
          studioTacActive.push(tacActive)

          monthlyTotals[m].ist += ist
          monthlyTotals[m].plan += plan
          monthlyTotals[m].new += d.newSales || 0
          monthlyTotals[m].renewals += d.renewals || 0
          monthlyTotals[m].cancellations += cancelCount

          if (tacActive !== null) {
            monthlyTotals[m].tacActive += tacActive
            monthlyTotals[m].validTacStudios++
          }

          lastIst = ist
        })

        returnData.push({
          center: studio.name,
          yearlyFluctuations,
          istData: studioIst,
          tacActiveData: studioTacActive,
          series:
            this.viewMode === "total"
              ? [
                  { name: "Soll", data: studioSoll },
                  { name: "Ist", data: studioIst },
                ]
              : [
                  { name: "Erneuerungen", group: "zugang", data: studioRenew },
                  { name: "Neukunden", group: "zugang", data: studioNew },
                  { name: "Kündigungen", group: "abgang", data: studioCancel },
                ],
          stats: {
            average: Math.round(studioIst.reduce((a, b) => a + b, 0) / (studioIst.length || 1)),
            currentMonth: {
              ist: studioIst[studioIst.length - 1] || 0,
              plan: studioSoll[studioSoll.length - 1] || 0,
              diffPercent: this.calculatePercentageDiff(studioIst[studioIst.length - 1], studioSoll[studioSoll.length - 1]),
            },
            monthlyGrowth: { value: (studioIst[studioIst.length - 1] || 0) - (studioIst[studioIst.length - 2] || 0) },
          },
        })
      })

      const totalYearlyFluctuations = availableYears
        .map((year) => {
          const chunk = monthsByYear[year].filter((m) => m <= currentMonthStr)
          if (chunk.length === 0) return null

          let sumFluct = 0
          let validMonths = 0

          chunk.forEach((m) => {
            const cancelCount = monthlyTotals[m].cancellations || 0
            // Nur zusammenrechnen, wenn wir für diesen Monat gültige TAC-Werte haben
            const activeMembers = monthlyTotals[m].validTacStudios > 0 ? monthlyTotals[m].tacActive : null

            if (activeMembers !== null && activeMembers > 0) {
              let fluct = (cancelCount * 12 * 100) / activeMembers
              sumFluct += fluct
              validMonths++
            }
          })

          const startMonth = moment(chunk[0], "YYYY-MM").format("MMM")
          const endMonth = moment(chunk[chunk.length - 1], "YYYY-MM").format("MMM")

          return {
            year: year,
            months: startMonth === endMonth ? startMonth : `${startMonth} - ${endMonth}`,
            average: validMonths > 0 ? (sumFluct / validMonths).toFixed(1) : "0.0",
          }
        })
        .filter(Boolean)

      const lastM = categories[categories.length - 1]
      const prevM = categories[categories.length - 2]

      const mainSeries =
        this.viewMode === "total"
          ? [
              { name: "Geplant", data: categories.map((m) => monthlyTotals[m].plan) },
              { name: "IST", data: categories.map((m) => monthlyTotals[m].ist) },
            ]
          : [
              { name: "Erneuerungen", group: "zugang", data: categories.map((m) => monthlyTotals[m].renewals) },
              { name: "Neukunden", group: "zugang", data: categories.map((m) => monthlyTotals[m].new) },
              { name: "Kündigungen", group: "abgang", data: categories.map((m) => monthlyTotals[m].cancellations) },
            ]

      const totalStats = {
        totalYearlyFluctuations,
        istData: categories.map((m) => monthlyTotals[m].ist),
        tacActiveData: categories.map((m) => (monthlyTotals[m].validTacStudios > 0 ? monthlyTotals[m].tacActive : null)),
        currentMonth: {
          ist: monthlyTotals[lastM]?.ist || 0,
          plan: monthlyTotals[lastM]?.plan || 0,
          diffPercent: this.calculatePercentageDiff(monthlyTotals[lastM]?.ist, monthlyTotals[lastM]?.plan),
        },
        previousMonth: { month: prevM || "N/A", value: monthlyTotals[prevM]?.ist || 0 },
        average: Math.round(categories.map((m) => monthlyTotals[m].ist).reduce((a, b) => a + b, 0) / (categories.length || 1)),
        monthlyGrowth: {
          value: (monthlyTotals[lastM]?.ist || 0) - (monthlyTotals[prevM]?.ist || 0),
          percent: this.calculatePercentageDiff(monthlyTotals[lastM]?.ist, monthlyTotals[prevM]?.ist),
        },
        totalGrowth: {
          value: (monthlyTotals[lastM]?.ist || 0) - (monthlyTotals[categories[0]]?.ist || 0),
          percent: this.calculatePercentageDiff(monthlyTotals[lastM]?.ist, monthlyTotals[categories[0]]?.ist),
        },
      }
      return { categories, returnData, mainSeries, totalStats }
    },

    mainStats() {
      if (!this.centerData) return []
      const s = this.centerData.totalStats
      return [
        { label: "Letzter Monat", value: s.previousMonth.value.toLocaleString() + " Mitg.", subtext: `Stand im ${s.previousMonth.month}` },
        {
          label: "Aktueller Monat",
          value: s.currentMonth.ist.toLocaleString() + " Mitg.",
          trend: s.currentMonth.diffPercent,
          subtext: `Ziel: ${s.currentMonth.plan.toLocaleString()}`,
        },
        {
          label: "Wachstum (Monat)",
          value: (s.monthlyGrowth.value >= 0 ? "+" : "") + s.monthlyGrowth.value.toLocaleString(),
          trend: s.monthlyGrowth.percent,
          subtext: "Vs. Vormonat",
        },
        {
          label: "Wachstum (Gesamt)",
          value: (s.totalGrowth.value >= 0 ? "+" : "") + s.totalGrowth.value.toLocaleString(),
          trend: s.totalGrowth.percent,
          subtext: `Über letzte ${this.selectedRange} Mon.`,
        },
      ]
    },
  },
  watch: {
    selectedRange() {
      this.isFiltering = true
      this.loadedStudios = []
      setTimeout(() => (this.isFiltering = false), 300)
    },
    viewMode() {
      this.isFiltering = true
      setTimeout(() => (this.isFiltering = false), 300)
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-container {
  padding: 10px;
}
.glass-control {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
}
.btn-text-white {
  color: white !important;
  font-weight: 500;
}
.stats-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 18px !important;
}
.main-chart-card {
  background: rgba(30, 30, 45, 0.6) !important;
  backdrop-filter: blur(15px);
  border-radius: 22px !important;
}

.studio-card {
  background: rgba(40, 40, 60, 0.8) !important;
  backdrop-filter: blur(20px);
  border-radius: 22px !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;

  .secondary-bg {
    background: rgba(0, 0, 0, 0.2);
  }

  .border-top {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
}

.empty-state-container {
  border: 2px dashed rgba(255, 255, 255, 0.1);
  border-radius: 20px;
}
.opacity-60 {
  opacity: 0.6;
}

.border-top-tooltip {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}
.custom-tooltip-modern {
  background: rgba(20, 20, 30, 0.95) !important;
  border-radius: 10px;
  color: #eee;
}

.custom-table {
  background: transparent !important;
}
.custom-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

.pointer {
  cursor: pointer;
}

.custom-select-small ::v-deep .v-input__control {
  min-height: 36px !important;
}
</style>
